/**
 * targets.js — the names a tutorial step is allowed to point at.
 *
 * Every class name in this app comes from a CSS module, so it is hashed at build
 * time and useless as a selector. A tutorial therefore points at elements by a
 * `data-tutorial` attribute, and this file is the single list of those names.
 *
 * To make a new element targetable:
 *   1. add a constant here,
 *   2. spread `tutorialAttr(TUTORIAL_TARGETS.MY_THING)` onto the element.
 *
 * `createBasics.definition.test.js` fails if a step references a name that is
 * not in this object, so a typo is caught by the suite rather than by a
 * tutorial that silently highlights nothing.
 */

export const TUTORIAL_TARGETS = Object.freeze({
    // Navbar + create modal
    NAV_CREATE: 'nav-create',
    CREATE_MODAL_TITLE: 'create-modal-title',
    CREATE_MODAL_CREATE_BUTTON: 'create-modal-create',

    // Scenario title page
    TITLE_TITLE: 'title-title',
    TITLE_DESCRIPTION: 'title-description',
    TITLE_PASSWORD: 'title-password',
    TITLE_HIDDEN: 'title-hidden',
    TITLE_LOGS: 'title-logs',
    TITLE_MAP: 'title-map',
    TITLE_COLLABORATORS: 'title-collaborators',
    TITLE_TAGS: 'title-tags',
    TITLE_NEXT: 'title-next',

    // Editor icon rail
    RAIL_CREATE_SITUATION: 'rail-create-situation',
    RAIL_PREVIEW: 'rail-preview',
    RAIL_CHECK_VALIDITY: 'rail-check-validity',
    RAIL_PUBLISH: 'rail-publish',
    RAIL_KEYS: 'rail-keys',

    // Keys panel
    KEYS_PANEL: 'keys-panel',
    KEYS_CREATE: 'keys-create',
    KEYS_HELP: 'keys-help',
    KEY_PICKER: 'key-picker',

    // Node palette + canvas
    PALETTE_PANEL: 'palette-panel',
    PALETTE_SITUATION: 'palette-situation',
    PALETTE_START: 'palette-start',
    PALETTE_END: 'palette-end',
    CANVAS: 'canvas',

    // Edit sidebar
    EDIT_TAB: 'edit-tab',
    EDIT_SITUATION_PREVIEW: 'edit-situation-preview',
    EDIT_SITUATION_TITLE: 'edit-situation-title',
    EDIT_SITUATION_TEXT: 'edit-situation-text',
    EDIT_SITUATION_DELETE: 'edit-situation-delete',
    EDIT_CHOICE_TITLE: 'edit-choice-title',
    EDIT_CHOICE_TEXT: 'edit-choice-text',
    EDIT_KEYS_ADD: 'edit-keys-add',
    EDIT_KEYS_REMOVE: 'edit-keys-remove',
    EDIT_KEYS_VISIBILITY: 'edit-keys-visibility',
    DELETE_MODAL_YES: 'delete-modal-yes',

    // Validity
    ERROR_ITEM: 'error-item',

    // Preview / player
    PREVIEW_START: 'preview-start',
    PREVIEW_SITUATION_TEXT: 'preview-situation-text',
    PREVIEW_CHOICE: 'preview-choice',
    PREVIEW_RESTART: 'preview-restart',
    PREVIEW_EXIT: 'preview-exit',
    PREVIEW_KEY_TRAY: 'preview-key-tray',
    PREVIEW_KEYS_PROMPT: 'preview-keys-prompt',
});

/** Every valid target name, for schema validation. */
export const TUTORIAL_TARGET_VALUES = Object.freeze(Object.values(TUTORIAL_TARGETS));

/**
 * Spreads the attributes that make an element targetable.
 *
 * @param {string} id      a TUTORIAL_TARGETS value
 * @param {string} [dataId] disambiguates repeated targets (a tab, an error row,
 *                          a choice bubble) so a step can point at exactly one.
 */
export const tutorialAttr = (id, dataId) =>
    dataId === undefined || dataId === null
        ? { 'data-tutorial': id }
        : { 'data-tutorial': id, 'data-id': String(dataId) };

export default TUTORIAL_TARGETS;
